import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../data/translations';
import { INVESTMENT_TYPES, getTaxConfig } from '../utils/taxEngine';

const AppContext = createContext();

// Exchange rate RON to EUR (approximate)
const EXCHANGE_RATE = 5.0;

const DEFAULT_INPUTS = {
  initialDeposit: 10000,
  monthlyContribution: 500,
  annualInterestRate: 7,
  years: 30,
  compoundingFrequency: 12,
  contributionFrequency: 12,
  investmentType: INVESTMENT_TYPES.UNIT_LINKED,
  includeTaxes: true,
  adminFeeRate: 1.5, // Comision administrativ (%)
  pauseEnabled: false,
  pauseStartYear: 0,
  pauseEndYear: 0,
};

export const AppProvider = ({ children }) => {
  // Theme state
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      return saved || 'light';
    }
    return 'light';
  });
  
  // Language state
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('language');
      return saved || 'ro';
    }
    return 'ro';
  });
  
  // Currency state - Always start with RON, user can switch to EUR
  const [currency, setCurrency] = useState('RON');
  
  // Calculator inputs
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  
  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);
  
  // Save language preference
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);
  
  // Currency is not saved - always starts with RON
  
  // Toggle theme
  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);
  
  // Toggle language
  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'ro' ? 'en' : 'ro');
  }, []);
  
  // Toggle currency
  const toggleCurrency = useCallback(() => {
    setCurrency(prev => prev === 'RON' ? 'EUR' : 'RON');
  }, []);
  
  // Get translation
  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.ro[key] || key;
  }, [language]);
  
  // Format currency with conversion
  const formatMoney = useCallback((value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    
    const displayValue = currency === 'EUR' ? value / EXCHANGE_RATE : value;
    const currencySymbol = currency === 'EUR' ? 'EUR' : 'RON';
    
    return new Intl.NumberFormat(language === 'ro' ? 'ro-RO' : 'en-US', {
      style: 'currency',
      currency: currencySymbol === 'EUR' ? 'EUR' : 'RON',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(displayValue);
  }, [currency, language]);
  
  // Update single input
  const updateInput = useCallback((key, value) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  }, []);
  
  // Reset to defaults
  const resetInputs = useCallback(() => {
    setInputs(DEFAULT_INPUTS);
  }, []);
  
  // Get tax config for current investment type
  const taxConfig = getTaxConfig(inputs.investmentType);
  
  const value = {
    // Theme
    theme,
    setTheme,
    toggleTheme,
    
    // Language
    language,
    setLanguage,
    toggleLanguage,
    t,
    
    // Currency
    currency,
    setCurrency,
    toggleCurrency,
    formatMoney,
    EXCHANGE_RATE,
    
    // Inputs
    inputs,
    setInputs,
    updateInput,
    resetInputs,
    
    // Tax
    taxConfig,
  };
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export default AppContext;
